<template>
  <Teleport to="body">
    <Transition
      name="xm"
      :duration="MOTION_REGULAR_MS"
      @after-enter="focusIn"
      @after-leave="restoreFocus"
    >
      <div
        v-if="open"
        role="dialog"
        aria-modal="true"
        :aria-label
        class="fixed inset-0 z-40 grid place-items-center p-[--raw-edge-space]"
      >
        <!-- The slotted content's ✕ is the accessible close control; the
                     backdrop is a pointer-only duplicate, hidden from assistive tech.
                     It fades itself — see the style block for why not the parent. -->
        <button
          type="button"
          tabindex="-1"
          aria-hidden="true"
          data-extended-backdrop
          class="absolute inset-0 bg-[white]/10 backdrop-blur-[80px]"
          @click="emit('close')"
        />

        <!-- Width is a pending design pass; every padding is a token from the style
                     block below, and `pb-0` is load bearing — the bottom space belongs to the
                     scrolled content, not to this box. -->
        <div
          ref="cardRef"
          data-extended-card
          class="raw-glass relative flex max-h-[calc(90dvh-var(--raw-edge-space)*2)] w-[min(840px,100%)] flex-col rounded-[1.5rem] px-[--raw-panel-space] pb-0 pt-[--raw-card-block-space]"
        >
          <slot />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'

import { onKeyStroke } from '@vueuse/core'

import { useModalState } from '~/scopes/campaign/composables/useModalState'
import { MOTION_REGULAR_MS } from '~/scopes/campaign/const/config.const'

// A wide, centred, distraction-free modal: max height, inner scroll, blurred backdrop.
// Mechanically the `IntroModal` family — fade + scale, no FLIP.
//
// Deliberately dumb: `open` is the caller's (router-driven), and there is no tier gate
// here. Its first consumer is desktop/tablet-only, but the take-action extended modal
// coming next may well want phone — a hard-coded breakpoint inside a "shell" is
// behaviour the next consumer can't see. The consumer computes `open`.
//
// Note what's absent: no `data-map-inset`. This covers the whole viewport, so it takes
// no camera inset — `cameraPadding()` reads that attribute as the *tablet left-panel
// width*, unconditionally, and a full-bleed "panel" would squeeze framing into a sliver.
// For the same reason the reader stays out of BGMap's watch tuple, which already
// excludes the video/cinema overlays. See .claude/rules/article-extended.md.

const props = defineProps<{
  /** Whether the modal is shown. Owned by the caller (router-driven). */
  open: boolean
  /** Accessible name — the slotted content carries its own visible header. */
  ariaLabel?: string
}>()

const emit = defineEmits<{ close: [] }>()

const { activeCinema } = useModalState()

const cardRef = ref<HTMLElement | null>(null)
// Element focused right before the modal opened (the extend button), so we can
// restore focus on close. It stays connected — the panel behind never unmounts.
const opener = ref<HTMLElement | null>(null)

watch(
  () => props.open,
  (isOpen, wasOpen) => {
    if (isOpen && !wasOpen)
      opener.value = (document?.activeElement as HTMLElement | null) ?? null
  },
)

function focusIn() {
  void nextTick(() => {
    const root = cardRef.value
    if (!root) return
    const target =
      root.querySelector<HTMLElement>(
        'button, [href], input, [tabindex]:not([tabindex="-1"])',
      ) ?? root
    target.focus()
  })
}

function restoreFocus() {
  const el = opener.value
  if (el?.isConnected) el.focus()
  opener.value = null
}

// No focus trap and no scroll lock, matching the stance in `ResponsiveModal`: there is
// no trap anywhere in this app, `focus-trap` isn't a dependency, and `inert` on the app
// root would leak (this is teleported to `body`, so the other panels are siblings, not
// descendants). `aria-modal="true"` scopes assistive tech. Nothing scrolls the document
// either — every surface is fixed with its own Lenis viewport, and the backdrop covers
// Mapbox's canvas. Known a11y gap, recorded rather than half-solved.
//
// Escape goes to the topmost layer only: cinema mode sits above this, so it answers
// first (see the ladder in `ResponsiveModal`).
onKeyStroke('Escape', (event) => {
  if (!props.open || activeCinema.value) return
  event.preventDefault()
  emit('close')
})
</script>

<style scoped>
/* Every padding of this card in one place — the dials to turn when design settles.

   The gutter drives the card's `px-`, LenisScroll's scrollport and every full-bleed child
   inside, so they move together. It's wider than a panel's 16px default
   (`assets/css/layout.css`) since the card is wider, and it takes two values because this
   shell has a phone consumer, take action: 48px on a 343px card leaves a 247px measure, which
   is not a column. The reader never mounts below 768, so the max-md value is inert for it.

   The vertical frame is one number — the card's `pt-` and, through the scroll's bottom space,
   the gap you land on at the end of the scroll — so the box is symmetric by construction
   rather than by two values agreeing. It can't be a `pb-` here: a padding on this box shows
   for the whole scroll instead of at its end (`UI/LenisScroll.vue`). The scroll's top space is
   the lead-in under the static header, not part of the frame.
   See .claude/rules/article-extended.md. */
[data-extended-card] {
  --raw-panel-space: 24px;
  --raw-card-block-space: 16px;
  --raw-scroll-space-top: 28px;
  --raw-scroll-space-bottom: var(--raw-card-block-space);

  @media screen(md) {
    --raw-panel-space: 48px;
    --raw-card-block-space: 40px;
  }
}

/* Each child fades ITSELF; the root never animates. This is the canonical explanation for
   the overlays that do it (here, `IntroModal`, `NativeVideo`'s cinema backdrop):

   An element with `opacity < 1` establishes a *backdrop root*, which leaves every
   descendant's `backdrop-filter` inert until the opacity lands back on exactly 1. Fading
   the container therefore made the 80px blur pop in on the last frame instead of easing —
   and it took the card's `.raw-glass` with it, since `.raw-glass` is a backdrop-filter too.
   An element's *own* opacity is safe: its backdrop is sampled and filtered before the
   element is composited (`NativeVideo`'s cinema backdrop is the working precedent).
   `transform` and `mask` on an ancestor create backdrop roots the same way.

   So: never move these rules up to the root, and animate opacity/transform only — a
   transform leaves `offsetWidth` alone, keeping LenisScroll's clip-path ResizeObserver
   quiet, where width/max-height would thrash it every frame. */
.xm-enter-active [data-extended-backdrop],
.xm-leave-active [data-extended-backdrop] {
  transition: opacity var(--raw-in-out-quart);
}
.xm-enter-from [data-extended-backdrop],
.xm-leave-to [data-extended-backdrop] {
  opacity: 0;
}

/* The card fades itself too, so its own glass survives the trip. Its descendants don't:
   the ✕ inside settles its glass as the card lands. A 36px button on the card's own pale
   background — the alternative (scale with no fade) would pop in fully opaque. */
.xm-enter-active [data-extended-card],
.xm-leave-active [data-extended-card] {
  transition:
    opacity var(--raw-in-out-quart),
    transform var(--raw-in-out-quart);
}
.xm-enter-from [data-extended-card],
.xm-leave-to [data-extended-card] {
  opacity: 0;
  transform: scale(96%);
}

/* Reduced motion drops the scale and keeps the cross-fade — a fade is the recommended
   substitute for movement, not something to shorten. Deliberately the same duration as
   above: `:duration` holds the element for a fixed span, so a shorter transition here
   would just leave it sitting still for the remainder. */
@media (prefers-reduced-motion: reduce) {
  .xm-enter-from [data-extended-card],
  .xm-leave-to [data-extended-card] {
    transform: none;
  }
}
</style>
